import { DeclarationType, getEngine, QuotePaymentStatus, QuoteType } from '@lio/rule-engine';
import { Box, Grid, ThemeProvider, Typography } from '@mui/material';
import { GroupedQuoteItem } from '@lio/rule-engine/utils/quote-details';
import { useSnackbar } from 'notistack';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { BillingCard } from '@pages/BindQuote/BillingCard';
import {
  DeclarationCard,
  electronicApprovalDeclaration,
  fraudStatementDeclaration,
} from '@pages/BindQuote/DeclarationCard';
import { QuoteProposalCard } from '@pages/BindQuote/QuoteProposalCard';
import { QuotePanelInstance } from '@components/quoteDetails/QuotePanelInstance/LazyQuotePanelInstance';
import { ToastType } from '@components/toast/ToastType.enum';
import { QuoteStatus, TQuoteApiResponse } from '@models/quotes';
import { FullscreenLoading, LoadingOverlay } from '@components/Loading';
import { useCheckQuoteActiveBinding, useCreateQuoteBinding } from '@services/quotes/quoteBinding';
import { useQuote } from '@services/quotes';
import { useUpdateQuoteItemAttributes } from '@services/quoteItem';
import { handleBeforeUnload } from '@utils/index';
import { CodeName, ValidationError } from '@models/quoteItem';
import { useStore } from '@store/GlobalStore';
import { writingCompanyIds } from '@models/productOffering';
import { BindingConditions } from '@pages/BindQuote/BindingConditions';
import { Layout } from '@layout/Layout';
import { useComposeTheme } from '@theme/theme';
import { useCreateQuoteDeclaration } from '@services/quotes/quoteDeclaration';
import { Panel } from '@components/panel/Panel';
import { useSearchDocuments } from '@services/document/useSearchDocuments';
import { DocumentStatus, DocumentType } from '@models/document';
import { DrawerList } from '@pages/QuoteDetails/constants/DrawerList.enum';
import { drawerListItems } from '@pages/QuoteDetails/constants/drawerListItems';
import { BindQuoteHeaderButtons } from '@pages/BindQuote/BindQuoteHeaderButtons';
import { shallow } from 'zustand/shallow';
import {
  getAttributeTypes,
  getUpdatedBillingMethodQuoteItemAttributes,
  getUpdatedQuoteItemAttributes,
  useGetFraudStatement,
} from '@pages/BindQuote/helpers';
import { BindQuoteHeader } from '@pages/BindQuote/BindQuoteHeader';
import { BindQuoteDialogs } from '@pages/BindQuote/BindQuoteDialogs';
import { BindQuoteActionButtons } from '@pages/BindQuote/BindQuoteActionButtons';
import { useHasMissingRequiredBeforeBindFields } from '@hooks/useHasMissingRequiredBeforeBindFields';
import { SubjectivitiesCard } from './SubjectivitiesCard';
import { ArrowLeft } from '@assets/svgs';
import { BottomNavManu } from '@components/bottomNavMenu/BottomNavMenu';
import { HeaderButtons } from '@components/header/HeaderButton';
import { BindQuoteModalType, BindQuoteModalTypes } from '@pages/BindQuote/BindQuoteDialogs/BindQuoteDialogs.constants';
import { AlertStatus } from '@models/alert';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { ExcessPremiumPanel } from '@pages/BindQuote/ExcessPremiumPanel/ExcessPremiumPanel';
import { GLOptionalCoveragesPanel } from '@pages/BindQuote/GLOptionalCoveragesPanel/GLOptionalCoveragesPanel';
import { getExcessAttributeTypes } from '@pages/BindQuote/helpers/getAttributeTypes';
import { Dialog } from '@components/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { quoteQueryKey } from '@services/quotes/keys';
import { ooseResolutionQueryKeys, type OoseResolutionDetail } from '@services/policy/useGetOoseResolution';
import { useClient } from '@utils/api-client';
import { GenerateQuoteCustomerAccessLinkButton } from '@components/button/GenerateQuoteCustomerAccessLinkButton';
import { terminalStatuses } from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import { ProductOfferingId } from '@models/newQuote';
import { QuoteWithStore, useCurrentQuoteStoreState, useQuoteStore } from '@store/Quote/QuoteStore';
import { ConfirmEmailModal } from '@components/modals/ConfirmEmailModal';
import { UnconfirmedEmailDialog } from '@components/modals/UnconfirmedEmailDialog';
import { getUpdateCustomerEmailAttributes } from '@pages/BindQuote/helpers/getUpdateCustomerEmailAttributes';
import { useAlertStore } from '@store/AlertStore';

export const BindQuotePage = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  invariant(quoteId);

  return (
    <QuoteWithStore quoteId={quoteId}>
      <BindQuoteView quoteId={quoteId} />
    </QuoteWithStore>
  );
};

const BindQuoteView = ({ quoteId }: { quoteId: string }) => {
  const theme = useComposeTheme('light');
  const [{ isLioSales, isInternalUser, isCustomerUser, isBroker }, roles] = useStore(
    (state) => [state.userAuth, state.roles],
    shallow,
  );
  const [panelInstances, billingValues, unsavedFieldsKeys, structuredQuote] = useQuoteStore(
    (state) => [state.panelInstances, state.billingCardFields, state.unsavedFieldsKeys, state.structuredQuote],
    shallow,
  );
  const isLoading = !structuredQuote;

  const { mutateAsync: bindQuote } = useCreateQuoteBinding();
  const client = useClient();
  const { createQuoteDeclaration } = useCreateQuoteDeclaration(quoteId);
  const { fraudStatement } = useGetFraudStatement(quoteId);

  const { quote } = useQuote(quoteId);
  const { isMobileScreen } = useGetScreenSize();
  const { isPending: isAttributesMutationLoading, updateQuoteItemAttributesAsync } = useUpdateQuoteItemAttributes();
  const navigate = useNavigate();
  const [modalType, setModalType] = useState<BindQuoteModalType | undefined>();
  const [navigateToNextPage, setNavigateToNextPage] = useState(false);
  const [isRating, setIsRating] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const quoteContextStore = useCurrentQuoteStoreState();
  const hasMissingRequiredBeforeBind = useHasMissingRequiredBeforeBindFields();

  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] = useState(false);
  const [isUnconfirmedEmailDialogOpen, setIsUnconfirmedEmailDialogOpen] = useState(false);
  const [emailConfirmationPending, setEmailConfirmationPending] = useState(false);
  const [pendingBindAfterEmailConfirmation, setPendingBindAfterEmailConfirmation] = useState(false);

  const [isPaymentDisabled, activeAlerts] = useAlertStore(
    (state) => [state.isPaymentDisabled, state.activeAlerts],
    shallow,
  );
  const quoteShouldShowPaymentDisabled = isPaymentDisabled && quote?.offering?.requirePreBindPayment ? true : false;

  // moving to an object to help keep flow readable above
  const sxStyles = {
    grid1: {
      paddingTop: isMobileScreen ? '24px' : '60px',
      paddingBottom: '80px',
      minHeight: '100%',
    },
  };

  const drawerItems = useMemo(() => {
    return drawerListItems.filter((it) => !it.hideOnBind && (!it.isShown || it.isShown(quote)));
  }, [quote]);

  const isEndorsement = quote?.quoteType === QuoteType.Endorsement;
  const documentPanelInstance = panelInstances.find((instance) => instance.panel.label === 'Documents');

  const policyQuoteItem = structuredQuote?.quoteItems.find(
    (quoteItem: any) => quoteItem.quoteItemType.codeName === CodeName.Policy,
  );

  const namedInsuredItem = structuredQuote?.quoteItems.find(
    (quoteItem: any) => quoteItem.quoteItemType.codeName === CodeName.NamedInsured,
  );
  const isSpecialtyWritingCompany =
    policyQuoteItem?.attributeTypes.WritingCompany?.attribute?.value === writingCompanyIds.specialty;
  const showExcessPremiumPanel =
    policyQuoteItem?.attributeTypes.LOIExcessLiabilityInd?.attribute?.value && isSpecialtyWritingCompany;
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();
  const sidebarOpen = searchParamsInTheUrl.has('sidebarItem');
  useCheckQuoteActiveBinding(quote as TQuoteApiResponse, { redirectToInProgress: true });
  const [{ areDeclarationFieldsFilled, declarationName }, setDeclarationFieldsStatus] = useState({
    areDeclarationFieldsFilled: false,
    declarationName: '',
  });
  const [areBillingFieldsFilled, setAreBillingFieldsFilled] = useState(false);

  const attributeTypes = useMemo(() => {
    if (!namedInsuredItem || !policyQuoteItem || !structuredQuote) {
      return null;
    }
    const ruleEngine = getEngine(structuredQuote);
    ruleEngine.setEngineInitialQuoteItemToPolicy();
    return getAttributeTypes({ namedInsuredItem, policyQuoteItem, ruleEngine, roles });
  }, [structuredQuote, namedInsuredItem, policyQuoteItem, roles]);

  const excessAttributeTypes = useMemo(() => {
    if (!namedInsuredItem || !policyQuoteItem || !structuredQuote) {
      return null;
    }
    const ruleEngine = getEngine(structuredQuote);
    ruleEngine.setEngineInitialQuoteItemToPolicy();
    return getExcessAttributeTypes({
      policyQuoteItem,
      ruleEngine,
      quoteItemId: policyQuoteItem.id,
      showExcessPremiumPanel: !!showExcessPremiumPanel,
    });
  }, [structuredQuote, policyQuoteItem, namedInsuredItem, showExcessPremiumPanel]);

  // Evaluate rule engine for CustomerEmail isRequired rule
  const emailIsRequired = useMemo(() => {
    if (!structuredQuote || !namedInsuredItem?.attributeTypes.CustomerEmail) {
      return true;
    }

    try {
      const ruleEngine = getEngine(structuredQuote);
      ruleEngine.setEngineInitialQuoteItem(namedInsuredItem.id);

      const isRequired = ruleEngine.execute(namedInsuredItem.attributeTypes.CustomerEmail.isRequired) as boolean;

      return isRequired;
    } catch (error) {
      console.warn('Failed to evaluate CustomerEmail isRequired rule:', error);
      return true;
    }
  }, [structuredQuote, namedInsuredItem]);

  const hasBinderAttributeType = !!policyQuoteItem?.attributeTypes.BinderInd;

  const hasUnsavedChanges = unsavedFieldsKeys.length > 0;
  const [documentGenerationId, setDocumentGenerationId] = useState<string | undefined>();
  const { documents } = useSearchDocuments({
    quoteId,
    documentTypeId: [DocumentType.QuoteProposal.id],
    documentGenerationId,
  });

  const lastDocumentCreated = documents?.items.length ? documents.items[0] : undefined;

  const isPendingOrInProgress =
    lastDocumentCreated?.documentGeneration?.status === DocumentStatus.Pending ||
    lastDocumentCreated?.documentGeneration?.status === DocumentStatus.InProgress;

  // TODO ignoring as part of refactor since quote-store-refactor will touch this
  const errorsByCodename = useMemo(() => {
    if (!structuredQuote || !policyQuoteItem) return {};
    const structuredQuoteWithUnsavedChanges = {
      ...structuredQuote,
      quoteItems: structuredQuote?.quoteItems.map((qi) => {
        if (qi.id !== policyQuoteItem?.id) {
          return qi;
        }
        const modifiedAttributes = Object.fromEntries(
          unsavedFieldsKeys.map((codeName) => {
            const unsavedAttributeType = policyQuoteItem.attributeTypes[codeName];
            const unsavedValue = Object.values(billingValues).find((v) => v.codeName === codeName)?.value;
            return [
              codeName,
              {
                ...unsavedAttributeType,
                attribute: {
                  isLocked: null,
                  isUpdatedByUser: null,
                  ...unsavedAttributeType?.attribute,
                  value: unsavedValue,
                },
              },
            ];
          }),
        );

        return {
          ...qi,
          attributeTypes: {
            ...qi.attributeTypes,
            ...modifiedAttributes,
          },
        };
      }) as GroupedQuoteItem[],
    };

    const engine = getEngine(structuredQuoteWithUnsavedChanges);
    engine.setEngineInitialQuoteItem(policyQuoteItem.id);

    const validationErrors = engine.validateQuoteItem(policyQuoteItem.id, { includeBilling: true });

    const errorsByCodename: Record<string, ValidationError> = Object.fromEntries(
      validationErrors.map((e) => [e.attributeType?.codeName ?? e.ruleId, e]),
    );

    return errorsByCodename;
  }, [billingValues, structuredQuote, policyQuoteItem, unsavedFieldsKeys]);

  useEffect(() => {
    if (isInternalUser && !sidebarOpen) {
      const newParams = new URLSearchParams(searchParamsInTheUrl);
      newParams.set('sidebarItem', 'messages');
      setSearchParamsInTheUrl(newParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (quote && ![QuoteStatus.Underwritten.id, QuoteStatus.ReadyToBind.id].includes(quote.status.id)) {
      navigate(`/quote/${quoteId}`, { replace: true });
    }
  }, [quote, quoteId, navigate]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (
      isPendingOrInProgress &&
      lastDocumentCreated.documentGeneration &&
      lastDocumentCreated.documentGeneration?.id !== documentGenerationId
    ) {
      setDocumentGenerationId(lastDocumentCreated.documentGeneration.id);
    }
  }, [isPendingOrInProgress, lastDocumentCreated, documentGenerationId, setDocumentGenerationId]);

  const handleSaveFraudStatement = async () => {
    if (areDeclarationFieldsFilled) {
      await createQuoteDeclaration({
        fraudStatementId: fraudStatement.id,
        declarationType: DeclarationType.FraudStatement,
        declaration: {
          statements: [electronicApprovalDeclaration, fraudStatementDeclaration],
          name: declarationName,
        },
      });
    }
  };

  if (isLoading || !structuredQuote || !quote || !attributeTypes || !fraudStatement) {
    return <FullscreenLoading />;
  }

  if (!policyQuoteItem) {
    console.warn('Policy quote item not found');
    return null;
  }
  if (!namedInsuredItem) {
    console.warn('NamedInsured quote item not found');
    return null;
  }

  const showBindingConditions = isSpecialtyWritingCompany && hasBinderAttributeType;
  const areAllFieldsDisabled = navigateToNextPage;

  const handleSaveBillingMethod = async (newValue: string) => {
    const request = getUpdatedBillingMethodQuoteItemAttributes({ quoteId, policyQuoteItem, attributeTypes, newValue });
    await updateQuoteItemAttributesAsync(request);
  };

  const handleSaveQuote = async ({ exitOnSave }: { exitOnSave?: boolean } = {}) => {
    await handleSaveFraudStatement();
    if (exitOnSave && !hasUnsavedChanges) {
      navigate('/');
      return;
    }
    const newBillingValues = quoteContextStore.billingCardFields;
    const request = getUpdatedQuoteItemAttributes({
      quoteId,
      policyQuoteItem,
      attributeTypes,
      billingCardFields: newBillingValues,
      isEndorsement,
      shouldIncludeBindAttributeType: isInternalUser && showBindingConditions,
    });
    await updateQuoteItemAttributesAsync(request);
    if (exitOnSave) {
      navigate('/');
    }
  };

  const handleConfirmEmail = async (email: string) => {
    setEmailConfirmationPending(true);
    try {
      if (!namedInsuredItem || !attributeTypes) {
        throw new Error('Named insured item or attribute types not found');
      }

      const request = getUpdateCustomerEmailAttributes({
        quoteId,
        namedInsuredItem,
        newEmail: email,
        customerEmailAttributeType: attributeTypes.emailAddress,
      });

      await updateQuoteItemAttributesAsync(request);
      setIsEmailConfirmationModalOpen(false);

      // If bind was pending, proceed with bind now
      if (pendingBindAfterEmailConfirmation) {
        setPendingBindAfterEmailConfirmation(false);
        await handleBindQuoteInternal();
      }
    } catch (error) {
      enqueueSnackbar('Failed to update email address', {
        variant: ToastType.Error,
      });
    } finally {
      setEmailConfirmationPending(false);
    }
  };

  const handleBindQuoteInternal = async () => {
    // The OoSE endorsement must NOT be bound independently — it is committed together with
    // all subsequent transaction resolutions in a single atomic operation from the resolution
    // wizard. Check only this quote's policy so an OoSE cached from another policy cannot
    // redirect an unrelated endorsement.
    if (isEndorsement) {
      // quote.policyId is present for both regular and staged endorsement quotes.
      const policyId = quote?.policyId;
      if (policyId) {
        try {
          const existingResolution = await queryClient.fetchQuery<OoseResolutionDetail | null>({
            queryKey: ooseResolutionQueryKeys.get(policyId),
            queryFn: () => client.get(`policy/${policyId}/oose-resolution`).json<OoseResolutionDetail | null>(),
            staleTime: 0,
          });
          if (existingResolution?.status === 'InProgress') {
            navigate(`/policy/${policyId}/oose-resolution`);
            return;
          }
        } catch {
          // Fall through to normal bind — it will also fail if the policy is blocked.
        }
      }
    }

    setNavigateToNextPage(true);
    let continueToBind = true;
    try {
      await handleSaveQuote();
    } catch (e) {
      continueToBind = false;
      setNavigateToNextPage(false);
      enqueueSnackbar('Saving quote before bind has failed', {
        persist: true,
        variant: ToastType.Error,
      });
    }
    if (quoteId && continueToBind) {
      try {
        await bindQuote({ quoteId });
        setNavigateToNextPage(false);
        navigate(`/quote/${quoteId}/bind-in-progress`);
      } catch (e) {
        try {
          const response = await (e as any).response.json();
          if (response?.error?.validationDetails?.type === 'BOR_CONFLICT') {
            setNavigateToNextPage(false);
            enqueueSnackbar('Quote Declined for BOR conflict', {
              persist: true,
              variant: ToastType.Error,
            });
            // Refetch updated quote status
            await queryClient.invalidateQueries({ queryKey: quoteQueryKey.get(quoteId) });
            return;
          }
        } catch (parsingError) {
          console.log('Error parsing HTTP Error', parsingError);
          // let outer catch handle
        }
        setNavigateToNextPage(false);
        enqueueSnackbar('Unable to submit bind request', {
          persist: true,
          variant: ToastType.Error,
        });
      }
    }
  };

  /**
   * Checks if email confirmation is required before binding the quote.
   * Returns true if the flow should stop (confirmation needed), false if bind can proceed.
   */
  const checkEmailConfirmationRequired = (): boolean => {
    const isRenewalQuote = quote?.quoteType === QuoteType.Renewal;
    const needsEmailConfirmation =
      (isCustomerUser || isBroker) &&
      isRenewalQuote &&
      namedInsuredItem &&
      !namedInsuredItem.attributeTypes['CustomerEmailConfirmedByUser']?.attribute?.value;

    if (needsEmailConfirmation) {
      setPendingBindAfterEmailConfirmation(true);
      setIsEmailConfirmationModalOpen(true);
      return true;
    }

    // Check if we need to show unconfirmed email dialog for internal users
    if (
      isInternalUser &&
      isRenewalQuote &&
      namedInsuredItem &&
      !namedInsuredItem.attributeTypes['CustomerEmailConfirmedByUser']?.attribute?.value
    ) {
      setIsUnconfirmedEmailDialogOpen(true);
      return true;
    }

    return false;
  };

  const handleBindQuote = async () => {
    if (checkEmailConfirmationRequired()) {
      return;
    }

    // Proceed with bind
    await handleBindQuoteInternal();
  };

  const handleContinuePayment = async () => {
    if (checkEmailConfirmationRequired()) {
      return;
    }

    // Proceed with bind
    await handleBindQuoteInternal();
  };

  const handleExit = () => {
    if (hasUnsavedChanges) {
      setModalType(BindQuoteModalTypes.exitDialog);
    } else {
      handleNavigate();
    }
  };

  const handleNavigate = () => {
    navigate(`/quote/${quoteId}`);
  };

  const toggleDrawer = (newValue: DrawerList | null) => {
    const newItem = drawerItems.find((item) => item.name === newValue);
    if (!newItem) return;

    const { sidebarItemSearchParamValue, sidebarTabSearchParamValue } = newItem;
    if (sidebarItemSearchParamValue) {
      const prevSidebarItem = searchParamsInTheUrl.get('sidebarItem');
      const newParams = new URLSearchParams(searchParamsInTheUrl);
      if (prevSidebarItem !== sidebarItemSearchParamValue) {
        newParams.set('sidebarItem', sidebarItemSearchParamValue);
        if (sidebarTabSearchParamValue) {
          newParams.set('sidebarTab', sidebarTabSearchParamValue);
        } else {
          newParams.delete('sidebarTab');
        }
        setSearchParamsInTheUrl(newParams, { replace: true });
      } else {
        newParams.delete('sidebarItem');
        newParams.delete('sidebarTab');
        setSearchParamsInTheUrl(newParams, { replace: true });
      }
    }
  };

  const showSideDrawer = isInternalUser && !isMobileScreen;

  const HeaderButtonsComponent = (
    <BindQuoteHeaderButtons
      areBillingFieldsFilled={areBillingFieldsFilled}
      areDeclarationFieldsFilled={areDeclarationFieldsFilled}
      onOpenChangelog={() => setModalType(BindQuoteModalTypes.changelog)}
      onShowMultiPolicyBindDialog={() => setModalType(BindQuoteModalTypes.multiPolicyBind)}
      onHandleBindQuote={handleBindQuote}
      onSaveQuote={handleSaveQuote}
      canNavigateAway={navigateToNextPage}
    />
  );

  const hideEditSubmission = isCustomerUser && !!quote?.referredToBroker && !!quote?.releasedToPolicyHolder;
  const showPaymentCompleteBanner = quote?.paymentStatus === QuotePaymentStatus.Complete;

  let banners = [];

  if (showPaymentCompleteBanner) {
    banners.push({
      type: AlertStatus.Warning,
      text: 'Payment for this policy succeeded but there was an error on bind. Please try binding again, or contact LIO Support at 1 (877) 546-7155',
    });
  }

  if (!hideEditSubmission) {
    banners.push({
      onClick: handleExit,
      icon: <ArrowLeft width='14px' height='14px' />,
      text: isEndorsement ? 'Modify Endorsement Details' : 'Edit Submission',
    });
  }

  if (quoteShouldShowPaymentDisabled) {
    const paymentBlockingAlerts = activeAlerts.filter((alert) => alert.blockPayments === true);

    paymentBlockingAlerts.forEach((alert) => {
      banners.push({
        type: AlertStatus.Incident,
        text:
          alert.message ||
          'Payments are currently disabled. Please contact LIO Support at 1 (877) 546-7155 for assistance.',
      });
    });
  }

  if (hasMissingRequiredBeforeBind) {
    banners.push({
      type: AlertStatus.Warning,
      text: "This quote can't be bound yet because there are unanswered questions in the submission. Please edit to continue.",
    });
  }

  const isBrokerOrInternalUser = isBroker || isInternalUser;
  const isSpecialEvents = quote.offering.id === ProductOfferingId.SpecialEvent;
  const isNewBusiness = quote && quote.quoteType === QuoteType.NewBusiness;
  const isNotTerminal = quote && !terminalStatuses.includes(quote.status.name);

  const shouldShowGenerateLinkButton = isNotTerminal && isNewBusiness && isBrokerOrInternalUser && isSpecialEvents;

  return (
    <ThemeProvider theme={theme}>
      <Layout
        title='Bind Quote'
        customHeader={
          <BindQuoteHeader
            hasUnsavedChanges={hasUnsavedChanges}
            canNavigateAway={navigateToNextPage}
            areDeclarationFieldsFilled={areDeclarationFieldsFilled}
            areBillingFieldsFilled={areBillingFieldsFilled}
            onHandleBindQuote={handleBindQuote}
            onShowMultiPolicyBindDialog={() => setModalType(BindQuoteModalTypes.multiPolicyBind)}
            onShowRolesManagementDialog={() => setModalType(BindQuoteModalTypes.rolesManagement)}
            onOpenChangelog={() => setModalType(BindQuoteModalTypes.changelog)}
            onSaveQuote={handleSaveQuote}
          />
        }
        config={{
          noDarkGradient: true,
        }}
        banners={banners}
        sideDrawerProps={
          showSideDrawer
            ? {
                items: drawerItems,
                toggleDrawer,
              }
            : undefined
        }
      >
        <Box sx={{ width: '100%' }}>
          <Grid
            container
            size={{ xs: 12 }}
            sx={[
              {
                alignContent: 'center',
                flexDirection: 'column',
                gap: 3,
                pl: showSideDrawer ? 'auto' : '0px',
              },
              sxStyles.grid1,
            ]}
          >
            <Grid sx={{ width: '100%', maxWidth: '848px' }}>
              <ThemeProvider theme={theme}>
                <Panel>
                  <Typography
                    variant='h4'
                    sx={isMobileScreen ? { display: 'flex', justifyContent: 'space-between' } : {}}
                  >
                    Review & Finalize
                  </Typography>
                  {isCustomerUser && (
                    <Grid container>
                      <Typography variant='bodySmall'>
                        Thank you for considering us for your insurance needs! Please check that the policy effective
                        date and all other details are accurate before proceeding. If you have any questions about your
                        quote, please contact {quote.brokerage?.name} at {quote.brokerageProducer.user.email}.
                      </Typography>
                    </Grid>
                  )}
                  {!isCustomerUser && (
                    <Grid container>
                      <Typography variant='caption'>
                        {isInternalUser
                          ? 'Please check that the policy effective date, brokerage and all other details are accurate before binding.'
                          : 'Thank you for considering us for your insurance needs! Please check that the policy effective date and all other details are accurate before proceeding.'}
                      </Typography>
                    </Grid>
                  )}

                  {shouldShowGenerateLinkButton && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mr: '26px', mb: 4, mt: 2 }}>
                      <Typography variant='caption' sx={{ display: 'block', mb: 0 }}>
                        You can grant access to the Customer to pay and bind this event by clicking the button below to
                        generate a unique link. Once generated, you will need to copy and paste the link into an email
                        and send it to the Customer. Each link can only be used once and will expire after 72 hours.
                      </Typography>
                      <GenerateQuoteCustomerAccessLinkButton
                        isOnReviewPage
                        quoteId={quoteId}
                        size='small'
                        sx={{
                          padding: '0px !important',
                          margin: 0,
                        }}
                      />
                    </Box>
                  )}
                  <Grid container sx={{ rowGap: '16px', marginTop: '16px' }}>
                    <Grid size={{ xs: 12 }}>
                      <QuoteProposalCard />
                      <BindQuoteActionButtons
                        onHandleOpenModal={() => setModalType(BindQuoteModalTypes.submissionSummary)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ width: '100%', minWidth: 0 }}>
                      <GLOptionalCoveragesPanel
                        policyQuoteItem={policyQuoteItem}
                        quoteId={quoteId}
                        setIsRating={setIsRating}
                      />
                    </Grid>
                    {showExcessPremiumPanel && excessAttributeTypes! && (
                      <Grid size={{ xs: 12 }} sx={{ width: '100%', minWidth: 0 }}>
                        <ExcessPremiumPanel
                          attributeTypes={excessAttributeTypes}
                          quoteId={quoteId}
                          policyQuoteItemId={policyQuoteItem.id}
                          setIsRating={setIsRating}
                        />
                      </Grid>
                    )}
                    {isInternalUser && !isEndorsement && showBindingConditions ? (
                      <Grid size={{ xs: 12 }}>
                        <BindingConditions attributeTypes={attributeTypes} />
                      </Grid>
                    ) : null}
                    {!isEndorsement ? (
                      <Grid size={{ xs: 12 }}>
                        <DeclarationCard
                          disabled={areAllFieldsDisabled}
                          statement={fraudStatement.statement}
                          onFieldsChange={setDeclarationFieldsStatus}
                        />
                      </Grid>
                    ) : null}
                    <Grid size={{ xs: 12 }}>
                      <BillingCard
                        hasUnsavedChanges={hasUnsavedChanges}
                        onFieldsChange={setAreBillingFieldsFilled}
                        attributeTypes={attributeTypes}
                        onSaveQuote={() => handleSaveQuote({ exitOnSave: false })}
                        isQuoteItemAttributesLoading={isAttributesMutationLoading}
                        onUpdateBillingMethod={handleSaveBillingMethod}
                        errorsByCodeName={errorsByCodename}
                        fieldsDisabled={areAllFieldsDisabled}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <SubjectivitiesCard quoteId={quoteId} quoteNumber={quote?.policyNumber?.policyNumber ?? ''} />
                    </Grid>
                  </Grid>
                </Panel>
              </ThemeProvider>
            </Grid>
            {documentPanelInstance && (
              // Documents Panel
              <Grid sx={{ width: '100%', maxWidth: '848px' }}>
                <QuotePanelInstance
                  quoteId={quoteId}
                  disabled={isLioSales}
                  panelInstance={documentPanelInstance}
                  level={0}
                  isBindQuotePanel
                />
              </Grid>
            )}
          </Grid>
          <BindQuoteDialogs
            quote={quote}
            onNavigate={handleNavigate}
            onBindQuote={handleBindQuote}
            onContinuePayment={handleContinuePayment}
            modalType={modalType}
            setModalType={setModalType}
          />
          <ConfirmEmailModal
            isOpen={isEmailConfirmationModalOpen}
            currentEmail={(namedInsuredItem?.attributeTypes.CustomerEmail?.attribute?.value as string) || ''}
            onCloseModal={() => {
              setIsEmailConfirmationModalOpen(false);
              setPendingBindAfterEmailConfirmation(false);
            }}
            onConfirm={handleConfirmEmail}
            isLoading={emailConfirmationPending}
            isBindPage={true}
            isRequired={emailIsRequired}
          />
          <UnconfirmedEmailDialog
            open={isUnconfirmedEmailDialogOpen}
            onClose={() => setIsUnconfirmedEmailDialogOpen(false)}
            onProceed={async () => {
              setIsUnconfirmedEmailDialogOpen(false);
              await handleBindQuoteInternal();
            }}
            isLoading={false}
          />
        </Box>
        {isRating && (
          <Box>
            <Dialog open={isRating} handleCloseDialog={() => {}}>
              <Grid
                container
                sx={{
                  height: '500px',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography variant='h4' color='text.subdued'>
                  Updating your quote.....
                </Typography>
                <Grid>
                  <LoadingOverlay />
                </Grid>
              </Grid>
            </Dialog>
          </Box>
        )}

        {isMobileScreen && (
          <BottomNavManu>
            <HeaderButtons buttons={[HeaderButtonsComponent]} actionItems={undefined} disableActionItems={undefined} />
          </BottomNavManu>
        )}
      </Layout>
    </ThemeProvider>
  );
};
